import { updateRenewVisaDocumentGroupNoteApi, useGetRenewVisaDocumentGroupInfo } from "@/api/services/main/renew-visa";
import { Switch } from "@/components/elements/Switch";
import { Textarea } from "@/components/elements/Textarea";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { background, divider, text } from "@/theme/colors";
import { rounded } from "@/theme/variables";
import { showError } from "@/utils/error";
import { toast } from "@/utils/toast";
import { Box, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

export type TUpdateDocumentNoteStepCardProps = {
  readonly?: boolean;
};

export const UpdateDocumentNoteStepCard: FC<TUpdateDocumentNoteStepCardProps> = ({ readonly }) => {
  const { employeeRenewVisaDocumentGroupId } = useParams();

  const { data: renewVisaDocumentGroup } = useGetRenewVisaDocumentGroupInfo();

  const [textareaValue, setTextAreaValue] = useState(renewVisaDocumentGroup?.note ?? "");

  const queryClient = useQueryClient();

  const [checkedSwitch, setCheckedSwitch] = useState<boolean>(renewVisaDocumentGroup?.isEnabledNote || false);

  useEffect(() => {
    if (textareaValue !== renewVisaDocumentGroup?.note) {
      setTextAreaValue(renewVisaDocumentGroup?.note ?? "");
    }

    if (checkedSwitch !== renewVisaDocumentGroup?.isEnabledNote) {
      setCheckedSwitch(renewVisaDocumentGroup?.isEnabledNote || false);
    }
  }, [renewVisaDocumentGroup]);

  const onSubmitRenewVisaDocumentGroupNote = async () => {
    if (!employeeRenewVisaDocumentGroupId || textareaValue === renewVisaDocumentGroup?.note) return;
    try {
      const rs = await updateRenewVisaDocumentGroupNoteApi(employeeRenewVisaDocumentGroupId, { note: textareaValue });
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RENEW_VISA_DOCUMENTS.FETCH_GROUP_DETAIL] });
    } catch (error) {
      showError(error);
    }
  };

  const handleOnChangeSwitch = async (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    if (!employeeRenewVisaDocumentGroupId) return;
    setCheckedSwitch((prev) => !prev);
    if (!checked) {
      setTextAreaValue("");
    }
    await updateRenewVisaDocumentGroupNoteApi(employeeRenewVisaDocumentGroupId, {
      isEnabledNote: checked,
      note: !checked ? "" : undefined,
    });
    await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RENEW_VISA_DOCUMENTS.FETCH_GROUP_DETAIL] });
  };

  return (
    <>
      <Stack flexDirection="row" my={3} gap={2}>
        <Typography variant="body14" color={text.secondary}>
          メモ欄を表示
        </Typography>
        <Switch disabled={readonly} checked={checkedSwitch} onChange={handleOnChangeSwitch} />
      </Stack>
      {checkedSwitch && (
        <Stack borderRadius={rounded.sm} border={`1px solid ${divider.middle}`} overflow="hidden">
          <Box bgcolor={background.secondary} py={1.5} px={2}>
            <Typography variant="sub16Bold">メモ欄</Typography>
          </Box>
          <Stack gap={2} py={2} px={3} borderTop={`1px solid ${divider.middle}`}>
            <Typography variant="body14" color={text.secondary}>
              その他メモとしてお使いください。入力したテキストは自動保存されます。
            </Typography>
            <Textarea
              disabled={readonly}
              value={textareaValue}
              onChange={(e) => setTextAreaValue(e.target.value)}
              onBlur={() => onSubmitRenewVisaDocumentGroupNote()}
            />
          </Stack>
        </Stack>
      )}
    </>
  );
};
